import { faDownload, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT, t } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ActionIcon, Group, Input, Stack, Table } from "@mantine/core";
import {
  ActionIconLink,
  ButtonLink,
  LoadingSuspense,
} from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo, useState } from "react";

import { api } from "@/api.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/projects/")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Projects`,
      },
    ],
  }),
});

const Projects: FC<{ search: string }> = ({ search }) => {
  const { data } = useSuspenseQueryPostgrest(
    useMemo(() => {
      let q = api.from("project").select("*,is_member,customer(name)");
      if (search) {
        q = q.or(`identifier.ilike.%${search}%,name.ilike.%${search}%`);
      }
      return q;
    }, [search]),
  );

  const { i18n } = useLingui();

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Trans>Identifier</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Name</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Start</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>End</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Customer</Trans>
          </Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.map((d) => (
          <Table.Tr key={d.id}>
            <Table.Td>{d.identifier}</Table.Td>
            <Table.Td>{d.name}</Table.Td>
            <Table.Td>{i18n.date(d.start)}</Table.Td>
            <Table.Td>{i18n.date(d.end)}</Table.Td>
            <Table.Td>{d.customer?.name}</Table.Td>
            <Table.Td>
              <ActionIcon.Group>
                <ActionIconLink
                  to="/projects/$identifier"
                  params={{ identifier: d.identifier }}
                  color="green"
                >
                  <FontAwesomeIcon
                    icon={faFolderOpen}
                    color="var(--mantine-color-white)"
                  />
                </ActionIconLink>
                {d.is_member && (
                  <ActionIconLink
                    to="/flows/download/selection/$identifier"
                    params={{ identifier: d.identifier }}
                    variant="outline"
                    color="green"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </ActionIconLink>
                )}
              </ActionIcon.Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

function RouteComponent() {
  const [search, setSearch] = useState("");

  return (
    <Stack gap="lg">
      <Group>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t`Search projects...`}
        />
        <ButtonLink to="/projects/create">
          <Trans>Create</Trans>
        </ButtonLink>
      </Group>
      <LoadingSuspense>
        <Projects search={search} />
      </LoadingSuspense>
    </Stack>
  );
}
